/** API clients, secrets and the request log (M15-05–M15-07, M15-61). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, RotateCw } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime, relative, shortDate } from '../../lib/format';
import { Alert, Button, Card, EmptyState, Field, FilterBar, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';
import { ReasonDialog } from '../../components/ReasonDialog';
import { SecretReveal, errorText } from './shared';

interface Credential { id: string; prefix: string; createdAt: string; expiresAt: string | null; revokedAt: string | null; active: boolean }
interface Client { id: string; name: string; clientId: string; type: 'CONFIDENTIAL' | 'PUBLIC'; status: 'PENDING_APPROVAL' | 'ACTIVE' | 'DISABLED'; serviceRoleId: string; serviceRole: string; scopes: string[]; redirectUris: string[]; ipAllowlist: string[]; lastUsedAt: string | null; createdAt: string; credentials: Credential[] }
interface Scope { key: string; label: string }
interface Role { id: string; name: string }
interface LogRow { id: string; apiClientId: string; method: string; path: string; status: number; durationMs: number; errorCode: string | null; traceId: string | null; at: string }

const STATUS_TONE = { ACTIVE: 'ok', PENDING_APPROVAL: 'warning', DISABLED: 'critical' } as const;

function ClientForm({ client, onClose }: { client?: Client; onClose: (secret?: { clientId: string; clientSecret: string | null }) => void }): JSX.Element {
  const qc = useQueryClient();
  const scopes = useQuery({ queryKey: ['api-scopes'], queryFn: () => api<Scope[]>('/api-clients/scopes') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<Role[]>('/roles') });
  const [name, setName] = useState(client?.name ?? '');
  const [type, setType] = useState<Client['type']>(client?.type ?? 'CONFIDENTIAL');
  const [roleId, setRoleId] = useState(client?.serviceRoleId ?? '');
  const [picked, setPicked] = useState<string[]>(client?.scopes ?? []);
  const [redirects, setRedirects] = useState((client?.redirectUris ?? []).join('\n'));
  const [ips, setIps] = useState((client?.ipAllowlist ?? []).join('\n'));
  const save = useMutation({
    mutationFn: async (): Promise<{ clientId: string; clientSecret: string | null } | undefined> => {
      const body = { name, serviceRoleId: roleId, scopes: picked, redirectUris: redirects.split(/\s+/).filter(Boolean), ipAllowlist: ips.split(/[\s,]+/).filter(Boolean) };
      if (client) {
        await api(`/api-clients/${client.id}`, { method: 'PATCH', body });
        return undefined;
      }
      return api<{ clientId: string; clientSecret: string | null }>('/api-clients', { method: 'POST', body: { ...body, type } });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['api-clients'] });
      onClose(r);
    },
  });
  return (
    <Modal title={client ? `Edit ${client.name}` : 'Register an API client'} description="The client acts as a service account under the role you pick; scopes can only narrow that role." onClose={() => onClose()}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {save.error && <Alert tone="critical">{errorText(save.error)}</Alert>}
        <Field label="Name" required><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required minLength={2} placeholder="e.g. SM Cebu POS" /></Field>
        {!client && (
          <Field label="Kind">
            <select className={inputClass} value={type} onChange={(e) => setType(e.target.value as Client['type'])}>
              <option value="CONFIDENTIAL">System-to-system (client credentials)</option>
              <option value="PUBLIC">App acting for a user (authorization code + PKCE)</option>
            </select>
          </Field>
        )}
        <Field label="Service role" required hint="Approvals are never granted to an API client, whatever the role.">
          <select className={inputClass} value={roleId} onChange={(e) => setRoleId(e.target.value)} required>
            <option value="">Choose a role…</option>
            {roles.data?.filter((r) => r.name !== 'Owner').map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        <fieldset>
          <legend className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">Scopes <span className="text-red-500">*</span></legend>
          <div className="grid gap-1 sm:grid-cols-2">
            {scopes.data?.map((s) => (
              <label key={s.key} className="flex cursor-pointer items-start gap-2 rounded-lg p-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                <input type="checkbox" className="mt-0.5" checked={picked.includes(s.key)} onChange={(e) => setPicked(e.target.checked ? [...picked, s.key] : picked.filter((x) => x !== s.key))} />
                <span><code className="text-xs">{s.key}</code><span className="block text-xs text-slate-500">{s.label}</span></span>
              </label>
            ))}
          </div>
        </fieldset>
        {(type === 'PUBLIC' || client?.type === 'PUBLIC') && (
          <Field label="Redirect addresses" hint="One https:// address per line."><textarea className={inputClass} rows={2} value={redirects} onChange={(e) => setRedirects(e.target.value)} /></Field>
        )}
        <Field label="IP allowlist" hint="Optional. IPv4 addresses or CIDR ranges, one per line. Empty allows any address."><textarea className={inputClass} rows={2} value={ips} onChange={(e) => setIps(e.target.value)} placeholder="203.0.113.10&#10;198.51.100.0/24" /></Field>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => onClose()}>Cancel</Button>
          <Button type="submit" busy={save.isPending} disabled={!picked.length || !roleId}>{client ? 'Save' : 'Register'}</Button>
        </div>
      </form>
    </Modal>
  );
}

function RequestLog({ clients }: { clients: Client[] }): JSX.Element {
  const [clientId, setClientId] = useState('');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ['api-logs', clientId, errorsOnly, page],
    queryFn: () => api<{ rows: LogRow[]; total: number; pageSize: number }>(`/integrations/logs?page=${page}&pageSize=50${clientId ? `&apiClientId=${clientId}` : ''}${errorsOnly ? '&errorsOnly=true' : ''}`),
  });
  const name = new Map(clients.map((c) => [c.id, c.name]));
  return (
    <Card title="Request log" description="Method, path, status and timing only — never payloads. Kept 90 days.">
      <FilterBar>
        <select className={filterClass} value={clientId} onChange={(e) => { setClientId(e.target.value); setPage(1); }} aria-label="Client">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={errorsOnly} onChange={(e) => { setErrorsOnly(e.target.checked); setPage(1); }} /> Errors only</label>
      </FilterBar>
      {q.isLoading ? <Skeleton className="h-32 w-full" /> : q.data?.rows.length === 0 ? <p className="text-sm text-slate-500">No requests yet.</p> : (
        <>
          <TableWrap>
            <thead><tr><Th>When</Th><Th>Client</Th><Th>Request</Th><Th align="right">Status</Th><Th align="right">ms</Th><Th>Code</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 font-mono text-xs dark:divide-slate-800">
              {q.data?.rows.map((r) => (
                <tr key={r.id}>
                  <Td className="whitespace-nowrap font-sans text-slate-500">{dateTime(r.at)}</Td>
                  <Td className="font-sans">{name.get(r.apiClientId) ?? '—'}</Td>
                  <Td><span className="font-semibold">{r.method}</span> {r.path.replace('/api/v1', '')}</Td>
                  <Td align="right" className={r.status >= 400 ? 'font-semibold text-red-600' : 'text-emerald-700 dark:text-emerald-300'}>{r.status}</Td>
                  <Td align="right">{r.durationMs}</Td>
                  <Td><span title={r.traceId ?? undefined}>{r.errorCode ?? ''}</span></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          {q.data && q.data.total > q.data.pageSize && (
            <div className="mt-3 flex items-center justify-end gap-2 text-sm">
              <Button size="sm" variant="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
              <span className="text-slate-500">Page {page} of {Math.ceil(q.data.total / q.data.pageSize)}</span>
              <Button size="sm" variant="secondary" disabled={page * q.data.pageSize >= q.data.total} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export function ApiClientsPage(): JSX.Element {
  const { can } = useAuth();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['api-clients'], queryFn: () => api<Client[]>('/api-clients') });
  const [editing, setEditing] = useState<Client | 'new' | null>(null);
  const [secret, setSecret] = useState<{ title: string; items: { label: string; value: string }[] } | null>(null);
  const [disabling, setDisabling] = useState<Client | null>(null);
  const [error, setError] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: ({ path, method = 'POST', body }: { path: string; method?: 'POST' | 'DELETE'; body?: unknown }) => api<{ clientId?: string; clientSecret?: string; previousValidUntil?: string }>(path, { method, body }),
    onSuccess: (r) => {
      setError(null);
      setDisabling(null);
      void qc.invalidateQueries({ queryKey: ['api-clients'] });
      if (r.clientSecret && r.clientId) setSecret({ title: 'New client secret', items: [{ label: 'Client ID', value: r.clientId }, { label: 'Client secret', value: r.clientSecret }, ...(r.previousValidUntil ? [{ label: 'Previous secret works until', value: dateTime(r.previousValidUntil) }] : [])] });
    },
    onError: (e) => setError(errorText(e)),
  });
  const manage = can('integrations.manage');
  return (
    <>
      <PageHeader
        title="API Clients"
        description="Systems that call CoreX-lite: POS, HIS, payroll, e-commerce. Each acts under a service role and its scopes."
        actions={manage && <Button onClick={() => setEditing('new')}><Plus aria-hidden="true" className="h-4 w-4" /> Register client</Button>}
      />
      {error && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      {list.isLoading && <Skeleton className="h-40 w-full" />}
      {list.data?.length === 0 && <Card><EmptyState title="No API clients" description="Register a client to receive a client ID and secret for the OAuth token endpoint." /></Card>}
      <div className="space-y-4">
        {list.data?.map((c) => (
          <Card
            key={c.id}
            title={c.name}
            description={`${c.type === 'PUBLIC' ? 'App acting for users (PKCE)' : 'System-to-system'} · acts as ${c.serviceRole} · last used ${c.lastUsedAt ? relative(c.lastUsedAt) : 'never'}`}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip tone={STATUS_TONE[c.status]}>{c.status === 'PENDING_APPROVAL' ? 'Waiting for approval' : c.status.toLowerCase()}</StatusChip>
                {c.status === 'PENDING_APPROVAL' && can('users.manage') && <Button size="sm" busy={act.isPending} onClick={() => act.mutate({ path: `/api-clients/${c.id}/approve` })}>Approve</Button>}
                {manage && <Button size="sm" variant="secondary" onClick={() => setEditing(c)}>Edit</Button>}
                {manage && c.type === 'CONFIDENTIAL' && c.status !== 'DISABLED' && <Button size="sm" variant="secondary" busy={act.isPending} onClick={() => act.mutate({ path: `/api-clients/${c.id}/rotate` })}><RotateCw aria-hidden="true" className="h-4 w-4" /> Rotate secret</Button>}
                {manage && (c.status === 'DISABLED'
                  ? <Button size="sm" variant="secondary" onClick={() => act.mutate({ path: `/api-clients/${c.id}/enable` })}>Enable</Button>
                  : <Button size="sm" variant="danger" onClick={() => setDisabling(c)}>Disable</Button>)}
              </div>
            }
          >
            <dl className="grid gap-3 text-sm sm:grid-cols-3">
              <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client ID</dt><dd className="font-mono text-xs">{c.clientId}</dd></div>
              <div className="sm:col-span-2"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Scopes</dt><dd className="flex flex-wrap gap-1">{c.scopes.map((s) => <code key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">{s}</code>)}</dd></div>
              {c.ipAllowlist.length > 0 && <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">IP allowlist</dt><dd className="font-mono text-xs">{c.ipAllowlist.join(', ')}</dd></div>}
              {c.redirectUris.length > 0 && <div className="sm:col-span-2"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Redirects</dt><dd className="break-all font-mono text-xs">{c.redirectUris.join(', ')}</dd></div>}
            </dl>
            {c.credentials.length > 0 && (
              <div className="mt-4">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Secrets</p>
                <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                  {c.credentials.map((k) => (
                    <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                      <span className="inline-flex items-center gap-2"><KeyRound aria-hidden="true" className="h-4 w-4 text-slate-400" /><code className="text-xs">{k.prefix}…</code> <span className="text-xs text-slate-500">created {shortDate(k.createdAt)}{k.expiresAt && !k.revokedAt ? ` · valid until ${dateTime(k.expiresAt)}` : ''}</span></span>
                      <span className="flex items-center gap-2">
                        <StatusChip tone={k.active ? 'ok' : 'neutral'}>{k.revokedAt ? 'revoked' : k.active ? 'active' : 'expired'}</StatusChip>
                        {manage && k.active && <Button size="sm" variant="ghost" onClick={() => act.mutate({ path: `/api-clients/${c.id}/credentials/${k.id}`, method: 'DELETE' })}>Revoke</Button>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        ))}
      </div>
      {list.data && list.data.length > 0 && <div className="mt-4"><RequestLog clients={list.data} /></div>}
      <Card title="Connecting" className="mt-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">Exchange the client ID and secret for a 60-minute token, then call the API with <code className="text-xs">Authorization: Bearer …</code>. Send an <code className="text-xs">Idempotency-Key</code> header on every POST, and <code className="text-xs">external_system</code> + <code className="text-xs">external_ref</code> on every transaction.</p>
        <pre className="mt-3 overflow-x-auto rounded-xl bg-slate-100 p-3 font-mono text-xs dark:bg-slate-950">{`curl -X POST ${window.location.origin}/api/v1/oauth/token \\
  -d grant_type=client_credentials -d client_id=cx_… -d client_secret=cxs_…`}</pre>
        <p className="mt-2 text-sm"><a className="font-medium text-brand-700 hover:underline dark:text-brand-100" href="/api/docs" target="_blank" rel="noreferrer">OpenAPI reference</a></p>
      </Card>

      {editing && <ClientForm client={editing === 'new' ? undefined : editing} onClose={(s) => { setEditing(null); if (s) setSecret({ title: 'Client registered', items: [{ label: 'Client ID', value: s.clientId }, ...(s.clientSecret ? [{ label: 'Client secret', value: s.clientSecret }] : [])] }); }} />}
      {secret && <SecretReveal title={secret.title} items={secret.items} onClose={() => setSecret(null)} />}
      {disabling && <ReasonDialog title={`Disable ${disabling.name}?`} description="Its tokens stop working within 30 seconds. Enabling it again needs an administrator's approval." confirmLabel="Disable" busy={act.isPending} error={error} onClose={() => setDisabling(null)} onConfirm={(reason) => act.mutate({ path: `/api-clients/${disabling.id}/disable`, body: { reason } })} />}
    </>
  );
}
